import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export const TermsAcceptanceDialog = () => {
  const { t } = useTranslation();
  const { user, needsTermsAcceptance, currentTermsVersionId, currentTermsVersion, refreshTermsStatus } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = !!user && needsTermsAcceptance;

  const handleAccept = async () => {
    if (!user || !currentTermsVersionId || !agreed) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error: acceptErr } = await supabase.from('terms_acceptances').insert({
        user_id: user.id,
        terms_version_id: currentTermsVersionId,
        user_agent: navigator.userAgent,
      });
      if (acceptErr && !acceptErr.message?.toLowerCase().includes('duplicate')) {
        throw new Error(acceptErr.message);
      }
      await refreshTermsStatus();
    } catch (err: any) {
      setError(err.message || 'Error');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        hideClose
        className="max-w-md w-[calc(100%-2rem)] rounded-2xl p-5 sm:p-6 text-base"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl">{t('setup_password.terms_only_title')}</DialogTitle>
          <DialogDescription className="text-sm">
            {t('setup_password.terms_only_subtitle')}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" className="py-3">
            <AlertCircle className="h-5 w-5" />
            <AlertTitle className="text-base">Error</AlertTitle>
            <AlertDescription className="text-sm">{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-3.5">
          <Checkbox
            id="agree-terms-modal"
            checked={agreed}
            onCheckedChange={(v) => setAgreed(!!v)}
            disabled={submitting}
            className="mt-0.5 h-5 w-5"
          />
          <label htmlFor="agree-terms-modal" className="text-sm leading-relaxed cursor-pointer">
            {t('setup_password.terms_checkbox', { version: currentTermsVersion ?? '—' })}{' '}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline inline-flex items-center gap-0.5 font-medium"
            >
              {t('setup_password.open_full_terms')}
              <ExternalLink className="h-4 w-4" />
            </a>
          </label>
        </div>

        <p className="text-sm text-muted-foreground text-center -mt-1">
          {t('setup_password.password_reminder')}
        </p>

        <Button onClick={handleAccept} disabled={!agreed || submitting} className="w-full text-base py-3 h-auto">
          {submitting ? (
            <>
              <LoadingSpinner className="mr-2 h-5 w-5" />
              {t('setup_password.saving')}
            </>
          ) : (
            t('setup_password.accept_only_btn')
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
